import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../App";

const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-background font-body flex flex-col">
    <header className="px-6 md:px-12 py-6">
      <Link to="/" className="inline-flex items-center gap-2">
        <img src="/logo.png" alt="PostWriter" className="w-8 h-8" />
        <span className="text-xl font-extrabold tracking-tighter font-headline text-foreground">
          PostWriter
        </span>
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 pb-16">
      <div className="w-full max-w-sm">{children}</div>
    </main>
  </div>
);

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(token ? "form" : "error"); // form | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post("/auth/reset-password", { token, password });
      setStatus("success");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "weak_password") {
        setError(detail.message);
      } else {
        // invalid/expired/already-used token — no form retry, send them
        // back to request a fresh link.
        setStatus("error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === "success") {
    return (
      <AuthShell>
        <Helmet>
          <title>Password reset — PostWriter</title>
        </Helmet>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <CheckCircle2 className="w-5 h-5 text-primary" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            Password reset
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Taking you to the login page…
          </p>
        </div>
      </AuthShell>
    );
  }

  if (status === "error") {
    return (
      <AuthShell>
        <Helmet>
          <title>Reset link invalid — PostWriter</title>
        </Helmet>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary mb-6">
            <XCircle className="w-5 h-5 text-destructive" />
          </div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-3">
            This link isn't valid
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed mb-8">
            It may have expired or already been used. Request a new one below.
          </p>
          <Button className="w-full" onClick={() => navigate("/forgot-password")}>
            Request a new link
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Helmet>
        <title>Reset password — PostWriter</title>
      </Helmet>
      <h1 className="font-headline text-2xl font-extrabold tracking-tight text-foreground mb-1">
        Set a new password
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Choose a new password for your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            required
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Reset password"}
          {!isSubmitting && <ArrowRight className="w-4 h-4" />}
        </Button>
      </form>
    </AuthShell>
  );
};

export default ResetPasswordPage;
